<script lang="ts">
  import { mdTextUrl, parseMdText, parseMdValid } from '../map/layers/spc'
  import type { MdHit } from '../map/layers/probe'
  import TextPopup from './TextPopup.svelte'

  /**
   * One SPC mesoscale discussion in full, opened from the map menu, in round
   * 2's frame (Stage 10.5, 2d): the summary as the headline, the discussion
   * set as paragraphs, and the wire text itself under "Full text" for the
   * people who read these as the forecaster wrapped them.
   *
   * This is the one place the app fetches a text product without going through
   * FastAPI. NWS alert text goes through the backend because `api.weather.gov`
   * asks every caller for a `User-Agent` with contact details and a browser
   * cannot set that header. SPC asks for nothing, sends
   * `Access-Control-Allow-Origin: *`, and serves 1.8 KB — a route here would
   * be a pure proxy adding a hop and buying nothing.
   *
   * The URL is built from the discussion number, never taken from the
   * feature's own `popupinfo`: that is a third party naming the address we
   * fetch, the same shape `app/api/alerts.py` refuses for CAP ids. (It is also
   * `http://`, so following it would be a mixed-content block besides.)
   *
   * The body is the longest untrusted string in the app. It is interpolated as
   * text so Svelte escapes it; there is no `{@html}` here and
   * `web/tests/panels.test.ts` asserts it across the tree.
   */

  interface Props {
    md: MdHit
    onClose: () => void
  }

  let { md, onClose }: Props = $props()

  let body = $state('')
  let error = $state('')

  /**
   * The `Valid 171946Z - 172215Z` line, which is the only authoritative window
   * these products publish — the map service carries no expiration field at
   * all, so until the text lands the menu's `folderpath` string is all there
   * is.
   */
  let valid = $derived.by(() => (body ? parseMdValid(body, Date.now()) : null))

  const fmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  let parsed = $derived(body ? parseMdText(body) : null)

  /** Issued and valid-until from the text's own window, then what it says
   *  it is about and how likely a watch is. */
  let meta = $derived(
    [
      { label: 'Issued', value: valid ? fmt.format(valid.from) : '', class: 'valid' },
      { label: 'Valid until', value: valid ? fmt.format(valid.to) : '', class: 'valid' },
      { label: 'Concerning', value: parsed?.concerning ?? '' },
      { label: 'Watch', value: parsed?.watch ?? '' },
    ].filter((m) => m.value),
  )

  let sections = $derived(
    parsed
      ? [
          { heading: 'Discussion', paras: parsed.discussion },
          { heading: 'Most probable', paras: parsed.probable },
          { heading: 'Attn', paras: parsed.attn },
        ].filter((s) => s.paras.length > 0)
      : [],
  )

  let expired = $derived(valid !== null && valid.to.getTime() < Date.now())

  $effect(() => {
    const wanted = md
    body = ''
    error = ''
    let cancelled = false
    void (async () => {
      try {
        const resp = await fetch(mdTextUrl(wanted.number))
        if (!resp.ok) throw new Error(String(resp.status))
        const text = await resp.text()
        if (!cancelled) body = text
      } catch {
        if (!cancelled) {
          error = 'Could not load this discussion — it may have expired.'
        }
      }
    })()
    return () => {
      cancelled = true
    }
  })
</script>

<TextPopup
  label="Mesoscale discussion"
  closeLabel="Close discussion"
  tag={expired ? 'Expired' : 'Mesoscale discussion'}
  tone={expired ? 'outline' : 'ink'}
  issuer="Storm Prediction Center"
  title={md.label.replace(/^MD\b/, 'Mesoscale Discussion')}
  {meta}
  areas={parsed?.areas ?? []}
  notice={expired ? 'This discussion has expired. It is shown for reference.' : ''}
  headline={parsed?.summary ?? (body ? '' : md.info)}
  {sections}
  source={`spc.noaa.gov · md${md.number}`}
  {onClose}
>
  {#if body}
    <!-- Fixed-width wire format: the forecaster wrapped it at 70 columns, and
         a product this reader does not recognise is shown only this way. -->
    {#if parsed}
      <details class="wire">
        <summary>Full text</summary>
        <pre class="text">{body}</pre>
      </details>
    {:else}
      <pre class="text">{body}</pre>
    {/if}
  {:else if !error}
    <p class="status">Loading the discussion…</p>
  {/if}
  {#if error}<p class="error">{error}</p>{/if}
</TextPopup>

<style>
  .wire {
    padding-top: 14px;
    border-top: 1px solid var(--color-divider);
  }

  summary {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
  }

  .text {
    white-space: pre-wrap;
    font-family: var(--font-mono);
    font-size: 12px;
    line-height: 1.45;
    margin: 10px 0 0;
  }

  .status {
    margin: 0;
    font-size: 14px;
    color: var(--color-muted);
  }

  .error {
    margin: 0;
    font-size: 14px;
  }
</style>
